import React, { useContext, SyntheticEvent, FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

//MUI
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";

//Custom Components
import useFormController from "@/components/useFormController";
import DynamicDialog from "@/ui/Dialog/DynamicDialog";
import DialogHeader from "@/ui/Dialog/DialogHeader";
import DialogFooter from "@/ui/Dialog/DialogFooter";
import DialogToolbarButtonClose from "@/ui/Dialog/ToolbarButtons/DialogToolbarButtonClose";
import ToolbarFillContent from "@/ui/Toolbar/ToolbarFillContent";
import DialogBody from "@/ui/Dialog/DialogBody";
import DialogActionButtonClose from "@/ui/Dialog/ActionButtons/DialogActionButtonClose";
import DialogActionButtonAdd from "@/ui/Dialog/ActionButtons/DialogActionButtonAdd";
import DialogActionButtonSave from "@/ui/Dialog/ActionButtons/DialogActionButtonSave";
import DialogToolbarMenuButton from "@/ui/Dialog/DialogToolbarMenuButton";
import DialogToolbarMenuItem from "@/ui/Dialog/DialogToolbarMenuItem";
import DialogContext from "@/ui/DialogContext/DialogContext";
import SnackbarContext from "@/ui/SnackbarContext/SnackbarContext";
import UserContext from "@/components/UserContext/UserContext";
import { GridContainer, GridItem } from "@/ui/Grid";
import FormContent from "@/components/FormContent";
import { Permissions } from "@/lib/permissions";
import FormAvatar from "@/ui/Form/FormAvatar";
import FormHeading from "@/ui/Form/FormHeading";
import FormHeadingExtendedData from "@/ui/Form/FormHeadingExtendedData";
import api from "@/lib/api";

//Types
import { ClosingDetails, FormControllerProps } from "@/@types/components/formController";
import { IDialogProps } from "@/@types/ui/DialogContext";
import { CrisisAlert, Delete } from "@mui/icons-material";

const ObilaznicaDialog: FunctionComponent<IDialogProps> = (props) => {
  const dialogContext = useContext(DialogContext);
  const snackbarContext = useContext(SnackbarContext);
  const userContext = useContext(UserContext);
  const { t } = useTranslation();

  const { dc, mode, form, onClose, preClose } = props;


  const { record, validation, dataChanged, fields, subModels, onFieldChange, doInsert, doUpdate, doClose, doRefresh, doDelete } =
    useFormController(props as FormControllerProps);

  const allowActivation = mode === "update" && userContext?.hasAnyPermission([Permissions.Administration]);

  const handleFieldChange = (value: any, source: string) => {
    if (onFieldChange) {
      onFieldChange(value, source);
    }
  };

  const handleInsert = (evt: SyntheticEvent) => {
    if (doInsert) {
      doInsert().then((result) => {
        if (result.success) {
          snackbarContext.showNotification(t("notifications.insert"), "success");
          close({ dataChanged: true, action: "insert" });
        } else if (result.error) {
          const msg = t("error.addRecord") + result.error;
          snackbarContext.showNotification(msg, "error");
        } else if (result.validationPass === false) {
          //do nothing
        }
      })
        .catch((resp) => {
          snackbarContext.showNotification(resp.error, "error");
        });
    }
  };

  const handleUpdate = (evt: SyntheticEvent) => {
    if (doUpdate) {
      doUpdate().then((result) => {
        if (result.success) {
          snackbarContext.showNotification(t("notifications.update"), "success");
          close({ dataChanged: true, action: "update" });
        } else if (result.error) {
          const msg = t("error.editRecord") + result.error;
          snackbarContext.showNotification(msg, "error");
        } else if (result.validationPass === false) {
          //do nothing
        }
      });
    }
  };


  const handleClose = (evt: SyntheticEvent) => {
    if (doClose) {
      doClose().then((result) => {
        if (result.success) {
          close({ dataChanged: false, action: "cancel" });
        } else {
          if (result.shouldSave) {
            handleUpdate(evt);
          } else if (result.canceled) {
            //do nothing
          }
        }
      }).catch(result => { console.log(result) });
    }
  };

  const handleActivate = (evt: SyntheticEvent) => {
    if (record.id !== null) {
      try {
        const apiInstance = new api();
        const url = `obilaznice/${record.id}/activate`
        apiInstance.Call(url, "patch").then((result) => {
          if (result) {
            const msg = t("messages.activate_record_success");
            snackbarContext.showNotification(msg, "success");
            doRefresh();
            onClose({ dataChanged: true, action: "update" });
          } else {
            const msg = t("error.activate_record");
            snackbarContext.showNotification(msg, "error");
          }
        });
      } catch (error) {
        const msg = t("error.activate_record");
        snackbarContext.showNotification(msg, "error");
      }
    }
  }

  const handleDeactivate = (evt: SyntheticEvent) => {
    if (record.id !== null) {
      try {
        const apiInstance = new api();
        const url = `obilaznice/${record.id}/deactivate`

        apiInstance.Call(url, "patch").then((result) => {
          if (result) {
            const msg = t("messages.deactivate_record_success");
            snackbarContext.showNotification(msg, "success");
            close({ dataChanged: true, action: "update" });
          } else {
            const msg = t("error.deactiavate_record");
            snackbarContext.showNotification(msg, "error");
          }
        });
      } catch (error) {
        const msg = t("error.deactivate_record");
        snackbarContext.showNotification(msg, "error");
      }
    }
  }

  const handleDelete = () => {
    doDelete()
      .then((result) => {
        if (result.success) {
          snackbarContext.showNotification(t("notifications.delete"), "success");
          close({ dataChanged: true, action: "delete" });
        } else if (result.error) {
          const msg = t("error.delete") + result.error;
          snackbarContext.showNotification(msg, "error");
        }
      })
  }

  const close = (result: ClosingDetails) => {
    if (result.dataChanged || dataChanged) {
      onClose({ dataChanged: true, action: result.action });
      if (preClose) {
        preClose(record);
      }
    }

    dialogContext.hideDialog();
  };

  const title = record.naziv ? (record.naziv as string + (record.active === false ? ` - ${t('common.deactivated_record')}` : '')) : t("titles.poi_1") as string;
  const disableSave = dataChanged ? false : true;


  const allowDelete = mode === "update" && userContext?.hasAnyPermission([Permissions.Administration, Permissions.HGSSMod, Permissions.HPSMod])

  const hasToolbarActions = allowDelete;

  const icon = <CrisisAlert />;

  let cmode = mode;
  if (record.active === false) {
    cmode = "view";
  }

  return (
    <DynamicDialog open={true} maxWidth={"lg"} fixedHeight={true} onClose={handleClose}>
      <DialogHeader>
        <Toolbar variant="dense" disableGutters={true}>
          <FormAvatar icon={icon} ariaLabel="poi" />
          <FormHeading header={title} subheaderContent={record.id ? <FormHeadingExtendedData record={record} /> : undefined} />
          <ToolbarFillContent />
          {hasToolbarActions ? (
            <DialogToolbarMenuButton>
              {mode === "update" && allowActivation && record.active === true ? (
                <DialogToolbarMenuItem
                  onClick={handleDeactivate}
                  label={t("buttons.deactivate")}
                  icon={<CancelIcon />}
                />
              ) : null}
              {mode === "update" && allowActivation && record.active === false ? (
                <DialogToolbarMenuItem
                  onClick={handleActivate}
                  label={t("buttons.activate")}
                  icon={<CheckCircleIcon />}
                />
              ) : null}
              {mode === "update" ? (
                <DialogToolbarMenuItem
                  onClick={handleDelete}
                  label={t("buttons.delete")}
                  icon={<Delete />}
                />
              ) : null}
            </DialogToolbarMenuButton>

          ) : null}

          <DialogToolbarButtonClose onClick={handleClose} />
        </Toolbar>
      </DialogHeader>
      <DialogBody>
        <Box m={2}>
          <GridContainer>
            <FormContent
              fieldNames={dc.getFormFieldsNames(form).filter((x: string) => { return x !== "id" && x !== "sigurnosne_poruke" })}
              record={record}
              validation={validation}
              fields={fields}
              onFieldChange={handleFieldChange}
              mode={cmode}
              columns={2}
              subModels={subModels}
            />
          </GridContainer>
        </Box>
      </DialogBody>
      <DialogFooter record={record}>
        <DialogActionButtonClose variant="outlined" onClick={handleClose} />
        {mode === "insert" ? (
          <DialogActionButtonAdd variant="contained" onClick={handleInsert} />
        ) : null}
        {mode === "update" ? (
          <DialogActionButtonSave variant="contained" onClick={handleUpdate} disabled={disableSave} />
        ) : null}
      </DialogFooter>
    </DynamicDialog>
  );
};

export default ObilaznicaDialog;
